"use client";

import React, { Children, useEffect, useRef, type CSSProperties, type ReactNode } from "react";

import useEmblaCarousel from "embla-carousel-react";

import { useResultsContext } from "@/components/common/ResultsSliderBase/ResultsContext";
import { cn, getArrowDirectionClicked } from "@/utils/general";
import { segmentEvent } from "@/utils/segmentfunctions";

export type ResultsEmblaOptions = { loop?: boolean; [key: string]: unknown };

type ResultsSliderEmblaProps = {
  children: ReactNode;
  className?: string;
  slideClassName?: string;
  /* Fixed gap in px between slides; replaces the default responsive padding layout. */
  gap?: number;
  options?: ResultsEmblaOptions;
  trackObject?: {
    module_location?: string;
  };
};

export function ResultsSliderEmbla({
  children,
  className,
  slideClassName = "basis-full lg:basis-[33.4%]",
  gap,
  options,
  trackObject = {},
}: ResultsSliderEmblaProps) {
  const {
    activeIndex,
    setActiveIndex,
    setSliderApi,
    slidesCount,
    autoplay,
    setAutoplay,
    autoplaySpeed,
    thumbnailSliderRef,
  } = useResultsContext();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start", ...options });
  const previousIndexRef = useRef(0);
  const autoplayTickRef = useRef(false);

  const moduleLocation = trackObject.module_location || "mid_fold";

  useEffect(() => {
    if (!emblaApi) return;

    setSliderApi({
      scrollPrev: () => emblaApi.scrollPrev(),
      scrollNext: () => emblaApi.scrollNext(),
      scrollTo: (index: number) => emblaApi.scrollTo(index),
    });

    return () => setSliderApi(null);
  }, [emblaApi, setSliderApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      const index = emblaApi.selectedScrollSnap();
      const previousIndex = previousIndexRef.current;
      previousIndexRef.current = index;
      setActiveIndex(index);
      thumbnailSliderRef?.slickGoTo?.(index);

      if (index === previousIndex) return;

      /* Autoplay ticks are not user interactions: no Segment event, autoplay keeps running. */
      if (autoplayTickRef.current) {
        autoplayTickRef.current = false;
        return;
      }
      setAutoplay(false);

      segmentEvent("Carousel Clicked", {
        module_location: moduleLocation,
        module_name: "Before & After",
        slide_number: index + 1,
        arrow: getArrowDirectionClicked(previousIndex, index, slidesCount),
      });
    };

    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, setActiveIndex, setAutoplay, slidesCount, moduleLocation, thumbnailSliderRef]);

  /* Only runs when context autoplay is on. Restarts per slide to stay in step with the thumbnail ring. */
  useEffect(() => {
    if (!emblaApi || !autoplay || slidesCount <= 1) return;

    const id = window.setTimeout(() => {
      autoplayTickRef.current = true;
      emblaApi.scrollNext();
    }, autoplaySpeed);

    return () => window.clearTimeout(id);
  }, [emblaApi, autoplay, autoplaySpeed, slidesCount, activeIndex]);

  return (
    <div
      ref={emblaRef}
      className={cn("overflow-hidden", className)}
      /* Stop autoplay as soon as the user grabs the track so a tick cannot fire mid-drag. */
      onPointerDown={() => setAutoplay(false)}
    >
      <div
        className={cn("flex items-stretch", gap === undefined && "-ml-4 lg:-ml-3")}
        style={gap === undefined ? undefined : ({ marginLeft: -gap } as CSSProperties)}
      >
        {Children.map(children, (child, index) => (
          <div
            key={index}
            className={cn(
              "min-w-0 shrink-0 grow-0",
              gap === undefined && "pl-4 lg:pl-3 lg:pr-1",
              slideClassName
            )}
            style={gap === undefined ? undefined : ({ paddingLeft: gap } as CSSProperties)}
          >
            {child}
          </div>
        ))}
      </div>
    </div>
  );
}
